import { apiSlice } from '../api/apiSlice'
import { ENDPOINTS } from '../../lib/endpoints'
import { DISCUSSIONS_PAGE_SIZE } from '../../lib/config'

export interface DiscussionAuthor {
  id: string
  profile: { displayName: string; avatarUrl: string | null; flair?: string | null } | null
}

export interface DiscussionCategory {
  id: string
  name: string
  slug: string
  color: string | null
  icon: string | null
}

export interface DiscussionTag {
  tag: { id: string; name: string; slug: string }
}

export interface Discussion {
  id: string
  title: string
  content: string
  authorId: string
  categories: DiscussionCategory[]
  viewCount: number
  commentCount: number
  voteScore: number
  hotScore: number
  isPinned: boolean
  isDeleted: boolean
  createdAt: string
  updatedAt: string
  author: DiscussionAuthor
  tags: DiscussionTag[]
  followers?: { userId: string }[]
  pins?: { userId: string }[]
}

export interface DiscussionsResponse {
  data: Discussion[]
  nextCursor: string | null
  hasMore: boolean
}

export interface DiscussionResponse {
  discussion: Discussion
}

export interface CreateDiscussionBody {
  title: string
  content: string
  categoryIds?: string[]
  tags: string[]
}

export interface SummaryResult {
  headline:   string
  summary:    string
  keyPoints:  string[]
}

export interface RelatedDiscussion {
  id:           string
  title:        string
  commentCount: number
  voteScore:    number
  createdAt:    string
  category:     { name: string; slug: string; color: string | null; icon: string | null } | null
  author:       { displayName: string; avatarUrl: string | null }
}

export const discussionsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTrendingTags: builder.query<{ tags: { id: string; name: string; slug: string; count: number }[] }, void>({
      query: () => ENDPOINTS.discussions.tags,
      providesTags: ['Discussion'],
      keepUnusedDataFor: 60 * 60,
    }),

    getDiscussions: builder.query<DiscussionsResponse, { sort?: string; categoryId?: string; categorySlug?: string; categorySlugs?: string; authorId?: string; tag?: string; search?: string; cursor?: string; limit?: number; dateFrom?: string; dateTo?: string; minVotes?: number }>({
      query: (params) => ({ url: ENDPOINTS.discussions.list, params: { limit: DISCUSSIONS_PAGE_SIZE, ...params } }),
      serializeQueryArgs: ({ queryArgs }) => { const { cursor: _cursor, ...rest } = queryArgs; return rest },
      merge: (currentCache, newItems, { arg }) => {
        if (!arg.cursor) return newItems
        return { ...newItems, data: [...currentCache.data, ...newItems.data] }
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
      providesTags: ['Discussion'],
    }),

    getDiscussion: builder.query<DiscussionResponse, string>({
      query: (id) => ENDPOINTS.discussions.get(id),
      providesTags: (_result, _err, id) => [{ type: 'Discussion', id }],
      structuralSharing: true,
    }),

    createDiscussion: builder.mutation<DiscussionResponse, CreateDiscussionBody>({
      query: (body) => ({ url: ENDPOINTS.discussions.create, method: 'POST', body }),
      invalidatesTags: ['Discussion'],
    }),

    updateDiscussion: builder.mutation<DiscussionResponse, { id: string; body: Partial<CreateDiscussionBody> }>({
      query: ({ id, body }) => ({ url: ENDPOINTS.discussions.update(id), method: 'PUT', body }),
      async onQueryStarted({ id, body }, { dispatch, queryFulfilled, getState }) {
        const state = getState() as any
        const queries = state?.api?.queries ?? {}
        const patches: any[] = []
        for (const key of Object.keys(queries)) {
          const entry = queries[key]
          if (entry?.endpointName !== 'getDiscussions' || !entry?.data?.data) continue
          if (!entry.data.data.find((d: any) => d.id === id)) continue
          const patch = dispatch(
            discussionsApi.util.updateQueryData('getDiscussions', entry.originalArgs, (draft) => {
              const d = draft.data.find((d) => d.id === id)
              if (!d) return
              if (body.title) d.title = body.title
              if (body.content !== undefined) d.content = body.content
            })
          )
          patches.push(patch)
        }
        try { await queryFulfilled } catch { patches.forEach((p) => p.undo()) }
      },
      invalidatesTags: (_result, _err, { id }) => [{ type: 'Discussion', id }, 'Discussion'],
    }),

    deleteDiscussion: builder.mutation<void, string>({
      query: (id) => ({ url: ENDPOINTS.discussions.delete(id), method: 'DELETE' }),
      invalidatesTags: ['Discussion'],
    }),

    voteDiscussion: builder.mutation<{ action: string; direction: string }, { id: string; direction: 'up' | 'down' }>({
      query: ({ id, direction }) => ({ url: ENDPOINTS.discussions.vote(id), method: 'POST', body: { direction } }),
      invalidatesTags: (_result, _err, { id }) => [{ type: 'Discussion', id }],
    }),

    followDiscussion: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: ENDPOINTS.discussions.follow(id), method: 'POST' }),
      invalidatesTags: (_result, _err, id) => [{ type: 'Discussion', id }],
    }),

    getFollowedDiscussions: builder.query<DiscussionsResponse, { cursor?: string; limit?: number }>({
      query: (params) => ({ url: ENDPOINTS.discussions.following, params: { limit: DISCUSSIONS_PAGE_SIZE, ...params } }),
      serializeQueryArgs: ({ queryArgs }) => { const { cursor: _cursor, ...rest } = queryArgs; return rest },
      merge: (currentCache, newItems, { arg }) => {
        if (!arg.cursor) return newItems
        return { ...newItems, data: [...currentCache.data, ...newItems.data] }
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
      providesTags: ['Discussion'],
    }),

    getForYouDiscussions: builder.query<DiscussionsResponse & { isPersonalized?: boolean; emptyInterests?: string[] }, { cursor?: string; limit?: number; categorySlugs?: string }>({
      query: (params) => ({ url: ENDPOINTS.discussions.foryou, params: { limit: DISCUSSIONS_PAGE_SIZE, ...params } }),
      serializeQueryArgs: ({ queryArgs }) => { const { cursor: _cursor, ...rest } = queryArgs; return rest },
      merge: (currentCache, newItems, { arg }) => {
        if (!arg.cursor) return newItems
        return { ...newItems, data: [...currentCache.data, ...newItems.data] }
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
      providesTags: ['Discussion'],
    }),

    unfollowDiscussion: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: ENDPOINTS.discussions.unfollow(id), method: 'DELETE' }),
      invalidatesTags: (_result, _err, id) => [{ type: 'Discussion', id }, 'Discussion'],
    }),

    summariseDiscussion: builder.mutation<{ summary: SummaryResult; cached: boolean }, string>({
      query: (id) => ({ url: ENDPOINTS.discussions.summarise(id), method: 'POST' }),
    }),

    getRelatedDiscussions: builder.query<{ related: RelatedDiscussion[] }, string>({
      query: (id) => ENDPOINTS.discussions.related(id),
      keepUnusedDataFor: 15 * 60,
    }),

    getSuggestedAgents: builder.query<{ agents: { id: string; name: string }[] }, string>({
      query: (discussionId) => ENDPOINTS.discussions.suggestedAgents(discussionId),
      providesTags: (_r, _e, id) => [{ type: 'Discussion', id }],
    }),

    togglePinDiscussion: builder.mutation<{ discussionId: string; isPinned: boolean }, string>({
      query: (id) => ({ url: ENDPOINTS.discussions.pin(id), method: 'PATCH' }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const patches: any[] = []
        const state = getState() as any
        const queries = state?.api?.queries ?? {}
        const currentUserId = state?.auth?.user?.id
        for (const key of Object.keys(queries)) {
          const entry = queries[key]
          if (entry?.endpointName !== 'getDiscussions' || !entry?.data?.data) continue
          const item = entry.data.data.find((d: any) => d.id === id)
          if (!item) continue
          const alreadyPinned = item.pins ? item.pins.length > 0 : false
          const patch = dispatch(
            discussionsApi.util.updateQueryData('getDiscussions', entry.originalArgs, (draft) => {
              const d = draft.data.find((d) => d.id === id)
              if (!d) return
              if (alreadyPinned) { d.pins = [] } else { d.pins = currentUserId ? [{ userId: currentUserId }] : [{ userId: '' }] }
            })
          )
          patches.push(patch)
        }
        try {
          const { data: result } = await queryFulfilled
          const state2 = getState() as any
          const queries2 = state2?.api?.queries ?? {}
          const uid = state2?.auth?.user?.id
          for (const key of Object.keys(queries2)) {
            const entry = queries2[key]
            if (entry?.endpointName !== 'getDiscussions' || !entry?.data?.data) continue
            dispatch(discussionsApi.util.updateQueryData('getDiscussions', entry.originalArgs, (draft) => {
              const d = draft.data.find((d) => d.id === id)
              if (!d) return
              d.pins = result.isPinned && uid ? [{ userId: uid }] : []
            }))
          }
        } catch { patches.forEach((p) => p.undo()) }
      },
      invalidatesTags: (_result, _err, id) => [{ type: 'Discussion', id }],
    }),
  }),
})

export const {
  useGetTrendingTagsQuery,
  useGetDiscussionsQuery,
  useGetFollowedDiscussionsQuery,
  useGetDiscussionQuery,
  useCreateDiscussionMutation,
  useUpdateDiscussionMutation,
  useDeleteDiscussionMutation,
  useVoteDiscussionMutation,
  useFollowDiscussionMutation,
  useUnfollowDiscussionMutation,
  useTogglePinDiscussionMutation,
  useGetForYouDiscussionsQuery,
  useSummariseDiscussionMutation,
  useGetRelatedDiscussionsQuery,
  useGetSuggestedAgentsQuery,
} = discussionsApi
